import { ref, getDownloadURL } from 'firebase/storage'

import { storage } from '../../../firebase'

import {
  getDisplayUrl,
  getPublicGifUrl as getCleanPublicGifUrl,
} from './mediaUrlService'

/**
 * Resolves a GIF URL for display, handling both emulator and production modes
 * Now uses the hosting infrastructure for better performance and consistency
 * @param gifUrl - The storage path returned from the backend (e.g., 'gifs/filename.gif')
 * @returns Promise<string> - The resolved URL for display
 */
export const resolveGifUrl = async (gifUrl: string): Promise<string> => {
  try {
    // Handle legacy full URLs (from before the refactoring fix)
    if (
      gifUrl.startsWith('https://storage.googleapis.com/') ||
      gifUrl.startsWith('https://firebasestorage.googleapis.com/')
    ) {
      // If it's already a full URL, return it directly
      return gifUrl
    }

    // Use the new hosting infrastructure for better performance
    const displayUrl = getDisplayUrl(gifUrl)

    // For the display URL, we can return it directly since it uses our hosting setup
    // This provides better performance than going through Firebase Storage tokens
    return displayUrl
  } catch (error) {
    console.warn(
      'Error with hosting URL, falling back to Firebase Storage:',
      error
    )

    // Fallback to Firebase Storage with tokens if needed
    try {
      const storageRef = ref(storage, gifUrl)
      const downloadUrl = await getDownloadURL(storageRef)

      // Fix emulator URLs to use localhost instead of 127.0.0.1 to avoid CORS issues
      if (downloadUrl.includes('127.0.0.1:9199')) {
        return downloadUrl.replace('127.0.0.1:9199', 'localhost:9199')
      }

      return downloadUrl
    } catch (storageError) {
      // Handle Firebase Storage emulator 127.0.0.1 invalid URL error
      if (
        storageError instanceof Error &&
        storageError.message.includes('127.0.0.1:9199')
      ) {
        // Extract project info from environment
        const projectId =
          import.meta.env.VITE_FIREBASE_PROJECT_ID || 'reaction-gif-app'
        const storageBucket =
          import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
          `${projectId}.appspot.com`

        // Manually construct the localhost URL for the emulator
        const encodedPath = encodeURIComponent(gifUrl)
        return `http://localhost:9199/v0/b/${storageBucket}/o/${encodedPath}?alt=media`
      }

      throw storageError
    }
  }
}

/**
 * Creates a clean public URL for sharing (no tokens)
 * Perfect for reaction GIFs shared in messaging apps
 * Now uses the hosting infrastructure for better SEO and performance
 * @param gifUrl - The storage path (e.g., 'gifs/filename.gif')
 * @returns string - Clean public URL with proper caching and range support for auto-play
 */
export const getPublicGifUrl = (gifUrl: string): string => {
  return getCleanPublicGifUrl(gifUrl)
}

// Re-export new media URL functions for convenience
export {
  getPublicMp4Url,
  getPreviewPageUrl,
  getDisplayUrl,
  getMediaUrls,
  extractGifId,
  createStoragePath,
} from './mediaUrlService'
